
import Input, { InputType } from "components/shared/inputs/input";
import type { Control, FieldErrors, UseFormReset, UseFormSetValue, UseFormWatch } from "react-hook-form";
import useTranslation from "utils/hooks/useTranslation";
import * as dd from 'constants/dropdown';
import { convertToSelect } from "utils";
import Tooltip from "components/shared/tooltip";
import Checkbox from "components/shared/inputs/checkbox";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useCallback, useEffect, useImperativeHandle, useState, forwardRef } from "react";
import { type CoolModeData } from "store/coolModeSlice";

import { updateForm } from 'store/projectSlice';

interface CoolModeProps {
    control: Control<any>;
    watch: UseFormWatch<any>;
    getValues?: any;
    setValue: UseFormSetValue<any>;
    validate?: () => Promise<any>;
    // onChange?: (e: any) => void;
    coolModeformStore: any
    reset: UseFormReset<any>
    getFieldState?: any
    errors: FieldErrors<any> 
}

export interface CoolModeRef {
    submit: () => Promise<any>;
}


const CoolMode = forwardRef<CoolModeRef, CoolModeProps>(({
        control,
        watch,
        setValue,
        // coolModeformStore: coolModeformStore,
        getValues,
        // getFieldState,
        // errors,
        // validate,
        // onChange
    }, ref) => {

    const { t, tHtml } = useTranslation();
    const defaultFormStore = useAppSelector((state) => state.coolMode);
    const [formStore, setFormStore] = useState<CoolModeData>();

    const dispatch = useAppDispatch();

    const data = {
        nbPeoples: dd.cool_nbPeoples,
        inertia: dd.cool_inertia,
        location: dd.cool_location,
        mitoyen: dd.cool_mitoyen,
        orientation: dd.cool_orientation,
        occultant: dd.cool_occultant,
        temperatures: dd.cool_temperature
    };

    const helpInertia = <Tooltip text="HELP_INERTIA" />;
    const helpLocation = <Tooltip text="HELP_LOCATION" />;
    const helpConfig = <Tooltip text="HELP_CONFIG" />;
    const helpRoof = <Tooltip text="HELP_ROOF" />;

    const projectStore = useAppSelector((state) => state.project);
    const coolModeForm = projectStore.project.forms.coolModeStep?.form;

    const [
        econome,
        econome_i18nCode,
        inertia,
        inertia_i18nCode,

        location,
        location_i18nCode,
        nbPeoples,
        temperature,
        config
      ] = watch([
        'coolMode.econome',
        'coolMode.econome_i18nCode',
        'coolMode.inertia',
        'coolMode.inertia_i18nCode',

        'coolMode.location',
        'coolMode.location_i18nCode',
        'coolMode.nbPeoples',
        'coolMode.temperature',
        'coolMode.config'
      ])

    console.log(getValues(), 'getValues()');

    useEffect(() => {
        async function fetchInitialData() {
          if (projectStore.project.forms.coolModeStep?.form != null) {
            setValue('coolMode', projectStore.project.forms.coolModeStep.form)
          }
        }
    
        fetchInitialData()
      }, [])

    useEffect(() => {
        // First try to get data from coolModeForm (projectStore), then fallback to defaultFormStore
        const sourceData = coolModeForm && Object.keys(coolModeForm).length > 0 ? coolModeForm : defaultFormStore.form;
        
        const coolModeData: CoolModeData = {
            nbPeoples: sourceData.nbPeoples,
            inertia: sourceData.inertia, 
            inertia_i18nCode: sourceData.inertia_i18nCode,
            location: sourceData.location, 
            location_i18nCode: sourceData.location_i18nCode,
            temperature: sourceData.temperature,
            econome: sourceData.econome, 
            econome_i18nCode: sourceData.econome_i18nCode,
            config: sourceData.config
        };
        setFormStore(coolModeData);
    }, [defaultFormStore, coolModeForm]);

    useEffect(() => {
        if (!formStore) return;

        const newFormStore = { ...formStore };
        const items: any[] = [];
         // if (ProjectStore.project.forms.coolModeStep?.form) {
        //     formStore.form = { ...formStore.form, ...ProjectStore.project.forms.coolModeStep.form };
        // }

        //if (ProjectStore.project.forms.heatModeStep.form.mitoyennete === 1) {
        // Initialize config with proper structure
        newFormStore?.config?.forEach((item: any, index: number) => {
            if (index < 4) {
                items.push({ ...item, mitoyen: 0, mitoyen_i18n: t('NO') });
            } else {
                items.push({ ...item });
            }
        });

        setFormStore({ ...newFormStore, config: items });
        onOrientationChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onOrientationChange = useCallback((e?: any) => {
        // if (e && onChange) onChange(e);
        if (!formStore) return;

        const newFormStore = { ...formStore };
        const items: any[] = [];
        
        // Get the current orientation value from the form
        const currentOrientation = e?.target?.value || e || newFormStore.config[0]?.orientation;
        
        // Keep the first item (orientation selector) with current value
        const updatedFirstItem = { 
            ...newFormStore.config[0], 
            orientation: currentOrientation,
            orientation_i18n: currentOrientation
        };
        items.push(updatedFirstItem);

        // Update orientation labels based on the selected orientation
        switch (currentOrientation) {
            case '1':
                items.push({ ...newFormStore.config[1], orientation_i18n: t('EST') });
                items.push({ ...newFormStore.config[2], orientation_i18n: t('SOUTH') });
                items.push({ ...newFormStore.config[3], orientation_i18n: t('WEST') });
                break;
            case '0':
                items.push({ ...newFormStore.config[1], orientation_i18n: t('NORTH_EST') });
                items.push({ ...newFormStore.config[2], orientation_i18n: t('SOUTH_EST') });
                items.push({ ...newFormStore.config[3], orientation_i18n: t('SOUTH_WEST') });
                break;
            default:
                // Keep existing values
                items.push({ ...newFormStore.config[1] });
                items.push({ ...newFormStore.config[2] });
                items.push({ ...newFormStore.config[3] });
        }
        
        // Add roof item
        items.push({ ...newFormStore.config[4], orientation_i18n: t('ROOF') });

        setFormStore({ ...newFormStore, config: items });
        
        // Update form fields using setValue
        if (setValue) {
            setValue(`coolMode.config[0].orientation`, currentOrientation);
            setValue(`coolMode.config[0].orientation_i18n`, currentOrientation);
            setValue(`coolMode.config[1].orientation_i18n`, items[1].orientation_i18n);
            setValue(`coolMode.config[2].orientation_i18n`, items[2].orientation_i18n);
            setValue(`coolMode.config[3].orientation_i18n`, items[3].orientation_i18n);
            setValue(`coolMode.config[4].orientation_i18n`, items[4].orientation_i18n);
        }
    }, [formStore, t, setValue]);

    // const onnbPeoplesChange = useCallback((_e?: any) => {
    //      //props.onChange(e); // NOSONAR

    //     // try {
    //     //     if (ProjectStore.project.forms.hpChoiceStep.form.nbOccupants !== '') {
    //     //         ProjectStore.project.forms.hpChoiceStep.form.nbOccupants = formStore.form.nbPeoples;
    //     //     }
    //     // } catch (ex: any) {
    //     //     //
    //     // }
    // // }, []);
    //     // if (e && onChange) onChange(e);
    // }, []);

    const onmitoyenChange = useCallback((e: any, index: number) => {
        // if (e && onChange) onChange(e);
        if (!formStore) return;

        const newFormStore = { ...formStore };
        const newConfig = [...newFormStore.config];
        
        // Get the new mitoyen value from the form event
        const newMitoyenValue = e?.target?.value || e;
        
        // Update the mitoyen value first
        newConfig[index] = {
            ...newConfig[index],
            mitoyen: newMitoyenValue
        };
        
        // Now apply the logic based on the new mitoyen value
        if (newMitoyenValue === '1') {
            newConfig[index] = {
                ...newConfig[index],
                surface: '0',
                occultant: '0',
                occultant_i18n: t('NO')
            };

            setValue && setValue(`coolMode.config[${index}].surface`, newConfig[index].surface, { shouldValidate: true, shouldDirty: true });

        } else if (newMitoyenValue === '0') {
            newConfig[index] = {
                ...newConfig[index],
                occultant: '2',
                occultant_i18n: t('EXTERIOR')
            };
        } else {
            newConfig[index] = {
                ...newConfig[index],
                surface: '',
                occultant: '',
                occultant_i18n: ''
            };

            setValue && setValue(`coolMode.config[${index}].surface`, newConfig[index].surface, { shouldValidate: true, shouldDirty: true });
        }

        setFormStore({ ...newFormStore, config: newConfig });
        
        // Also update the form field value using setValue
        if (setValue) {
            setValue(`coolMode.config[${index}].mitoyen`, newMitoyenValue, { shouldValidate: true });
            setValue(`coolMode.config[${index}].occultant`, newConfig[index].occultant, {shouldValidate: true, shouldDirty: true });
        }
    }, [formStore, t, setValue]);

    const onsurfaceChange = useCallback((e: any, index: number) => {
        // if (e && onChange) onChange(e);
        if (!formStore) return;

        const newFormStore = { ...formStore };
        const newConfig = [...newFormStore.config];
        
        // Get the new surface value from the form event
        const newSurfaceValue = e?.target?.value || e;
        console.log('onsurfaceChange called:', { newSurfaceValue, index });
        
        // Update the surface value first
        newConfig[index] = {
            ...newConfig[index],
            surface: newSurfaceValue
        };
        
        // Now apply the logic based on the new surface value
        if (newSurfaceValue === '0' || newSurfaceValue === 0) {
            newConfig[index] = {
                ...newConfig[index],
                occultant: '0',
                occultant_i18n: t('NO')
            };
        } else if (index >= 3 && newConfig[index].occultant === '0') {
            newConfig[index] = {
                ...newConfig[index],
                occultant: '2',
                occultant_i18n: t('EXTERIOR')
            };
        }

        setFormStore({ ...newFormStore, config: newConfig });
        
        // Also update the form field value using setValue
        if (setValue) {
            setValue(`coolMode.config[${index}].surface`, newConfig[index].surface, { shouldValidate: true, shouldDirty: true });
            setValue(`coolMode.config[${index}].occultant`, newConfig[index].occultant, { shouldValidate: true, shouldDirty: true });
            setValue(`coolMode.config[${index}].occultant_i18n`, newConfig[index].occultant_i18n, { shouldValidate: true, shouldDirty: true });
        }
    }, [formStore, t, setValue]);

    const submit = useCallback(async () => {
    //     if (!validate) return Promise.resolve();
    //     debugger;
    //     return validate()
    //         .then(async () => {
    //             debugger;
    //             // Update the form store with current values
    //             if (projectStore.project.forms.coolModeStep?.form != null) {
    //                 setValue('coolMode', projectStore.project.forms.coolModeStep.form)
    //               }
    //             // if (formStore) {
    //             //     // Here you would typically update the Redux store or call an API
    //             //     console.log('Form submitted with data:', formStore);
    //             // }
    //             return Promise.resolve();
    //         })
    //         .catch(err => {
    //             console.error('Form validation failed:', err);
    //             return Promise.reject(err);
    //         });
    // }, [validate, formStore]);
        
        if (formStore) {
            // debugger;
            const values = getValues();
            
            // Ensure hpChoiceStep form exists with required heatPumpType to prevent null errors
            // dispatch(
            //     updateForm({
            //         form: 'hpChoiceStep',
            //         data: { 
            //             form: { 
            //                 heatPumpType: '1', // Default value to prevent null error
            //                 captage: '',
            //                 utilisation: '',
            //                 energie: '',
            //                 typologie: '',
            //                 alimentation: '',
            //                 abonnement: '',
            //                 releveChaudiere: '',
            //                 typeECS: ''
            //             } 
            //         },
            //     } as any),
            // );
            
            dispatch(
                updateForm({
                    form: 'coolModeStep',
                    data: { form: { ...JSON.parse(JSON.stringify(values.coolMode)) } },
                } as any),
            )
        }
     
         return Promise.resolve();
    }, [formStore, setValue, coolModeForm]);

    // Expose the submit function to parent component
    useImperativeHandle(ref, () => ({
        submit
    }), [submit]);


    console.log('formStore', formStore);
    console.log('coolModeForm', coolModeForm);

    return (
        <>
            <group data-gap="20" data-direction="column">
                <group>
                    <group data-align="center" data-width="auto" data-gap="10" data-fit="1">
                        <text
                            data-weight="600"
                            data-text-size="large"
                            data-ellipsis=""
                            data-color="grey-color-dark"
                        >
                            {t("HABITAT_DESC")}
                        </text>
                    </group>
                </group>
                <group>
                    <group data-length="forcefit-1000" data-fit="1">
                        <Input
                            type={InputType.Select}
                            label="INERTIA"
                            requiredLabel={true}
                            control={control}
                            name="coolMode.inertia"
                            options={convertToSelect(data.inertia)}
                            translateValue={true}
                            disabled={!data.inertia}
                            allowSearch={true}
                            size="large"
                            placeholder={`${t("INERTIA")}`}
                            dataLength="autofit"
                            helpText="HELP_INERTIA"
                        />
                    </group>
                </group>
                <group>
                    <group data-length="forcefit-1000" data-fit="1" data-text-size="medium">
                        <Checkbox
                            name="coolMode.location"
                            label={<>{t("LOCATION")}</>}
                            checkbox
                            control={control}
                            //onChange={onSeasideChange}                           
                        />
                        {helpLocation}
                    </group>
                </group>
                <group>
                    <group data-length="forcefit-1000" data-fit="1" data-max-length="350">
                        <Input
                            type={InputType.Select}
                            label={"NB_PEOPLE"}
                            requiredLabel={true}
                            control={control}
                            name="coolMode.nbPeoples"
                            options={convertToSelect(data.nbPeoples)}
                            //translateValue={true}
                            disabled={!data.inertia}
                            allowSearch={true}
                            size="large"
                            placeholder={`${t("NB_PEOPLE")}`}
                            dataLength="autofit"
                            // onChange={onnbPeoplesChange}
                        //loading={isFetchingDepartments}
                        />
                    </group>
                </group>
                <group>
                    <group data-length="forcefit-1000" data-fit="1" data-max-length="350">
                        <Input
                            type={InputType.Select}
                            label={"DESIRED_TEMP_COOL"}
                            requiredLabel={true}
                            control={control}
                            name="coolMode.temperature"
                            options={convertToSelect(data.temperatures)}
                            //translateValue={true}
                            disabled={!data.inertia}
                            allowSearch={true}
                            size="large"
                            placeholder={`${t("DESIRED_TEMP_COOL")}`}
                            dataLength="autofit"
                            // onChange={onChange}
                        //loading={isFetchingDepartments}
                        />
                    </group>
                </group>
            </group>
            <group data-gap="20" data-direction="column">

                <group data-align="center" data-width="auto" data-gap="10">
                    <text
                        data-weight="600"
                        data-text-size="large"
                        data-ellipsis=""
                        data-color="grey-color-dark"
                    >
                        {t("HABITAT_CONFIG")}
                    </text>
                    {helpConfig}
                </group>
            </group>
            <group data-direction="column" data-gap="20">

                <group key="header"
                    data-gap="20"
                    data-adaptive="desktop-1000"
                //data-background="yellow"
                >
                    <group
                        data-max-length='180'
                        data-length="autofit"
                    >
                        <text data-ellipsis="" data-text-size="medium">{t("FACE")}<i data-space-horizontal="5" data-color="red">*</i></text>
                    </group>
                    <group
                        data-gap="20"
                        data-wrap="nowrap"
                        data-width="auto"
                        data-length="autofit"
                    >
                        <group
                            data-length="autofit"
                        >
                            <text data-ellipsis="" data-wrap="wrap" data-text-size="medium">{t("LENGTH")}<i data-space-horizontal="5" data-color="red">*</i></text>
                        </group>
                        <group
                            data-length="autofit"
                        >
                            <text data-ellipsis="" data-wrap="wrap" data-text-size="medium">{t("MITOYEN")}<i data-space-horizontal="5" data-color="red">*</i></text>
                        </group>
                    </group>
                    <group
                        data-gap="20"
                        data-wrap="nowrap"
                        data-width="auto"
                        data-length="autofit"
                    >
                        <group data-length="autofit" data-max-length='225'>
                            <text data-ellipsis="" data-wrap="wrap" data-text-size="medium">{t("SURFACE")}<i data-space-horizontal="5" data-color="red">*</i></text>
                        </group>
                        <group data-length="autofit">
                            <text data-ellipsis="" data-wrap="wrap" data-text-size="medium">{t("OCCULTANT")}<i data-space-horizontal="5" data-color="red">*</i></text>
                        </group>
                    </group>
                </group>
                <group data-gap="10" >
                    {formStore?.config?.map((_: any, index: number) => {
                        return (

                            <group key={index}
                                data-gap="20"
                            >
                                {index === 0 ?
                                    <group
                                        data-direction="column"
                                        data-length="forcefit-1000"
                                        data-min-length="100"
                                        data-max-length="180"
                                        data-fit="1"
                                        data-justify="end"
                                    >
                                        <group data-length="autofit" data-adaptive="mobile-1000">
                                            <text data-ellipsis="" data-text-size="medium">{t("FACE")}<i data-space-horizontal="5" data-color="red">*</i></text>
                                        </group>
                                        <group data-length="autofit">
                                            <Input
                                                type={InputType.Select}
                                                //label={t("ORIENTATION")}
                                                //label={<><text data-adaptive="mobile-1000">{t("ORIENTATION")}</text></>}
                                                label={<></>}
                                                requiredLabel={true}
                                                control={control}
                                                name={`coolMode.config[${index}].orientation`}
                                                options={convertToSelect(data.orientation)}
                                                translateValue={true}
                                                disabled={!data.inertia}
                                                allowSearch={true}
                                                size="large"
                                                placeholder={`${t("FACE")}`}
                                                dataLength="autofit"
                                                onChange={onOrientationChange}
                                                // icon={<img src={orientation} alt="orientation" />}

                                            //loading={isFetchingDepartments}
                                            />
                                        </group>
                                    </group>
                                    :
                                    <group
                                        data-direction="column"
                                        data-length="forcefit-1000"
                                        data-min-length="100"
                                        data-max-length="180"
                                        data-fit="1"
                                        data-justify="end"
                                    >
                                        {/* <Input
                                            type={InputType.Text}
                                            //label={formStore?.config[index]?.orientation_i18n}
                                            label={<></>}
                                            requiredLabel={true}
                                            control={control}
                                            name={`coolMode.config[${index}].orientation_i18n`}
                                            translateValue={true}
                                            disabled={true}
                                            allowSearch={true}
                                            size="large"
                                            placeholder={formStore?.config[index]?.orientation_i18n}
                                            dataLength="autofit"
                                            button={index === 4 ? <group >{helpRoof}</group> : undefined}
                                        /> */}

                                        <div 
                                            className="field" 
                                            data-length="autofit" 
                                            data-size="large">
                                                <label className="form_fields ">
                                                    <div className="data_label"></div>
                                                    <div className="field_cont" 
                                                    data-length="autofit">
                                                        <label 
                                                            data-space-horizontal="10" 
                                                            data-text-size="medium"
                                                            data-opacity="50"
                                                            >{formStore?.config[index]?.orientation_i18n}</label>
                                                            {index === 4 ? <group >{helpRoof}</group> : undefined}
                                                        <i style={{"background": "var(--hover)"}}></i>
                                                    </div>
                                                </label>
                                            </div>
                                    </group>
                                }

                                <group
                                    data-wrap="no"
                                    data-width="auto"
                                    //data-background="red"
                                    data-length="forcefit-1000"
                                    data-fit="1"
                                >
                                    <group
                                        data-length="autofit"
                                        data-direction="column"
                                        data-max-length="50%" data-space-right="10"
                                    >
                                        <group data-length="autofit" data-adaptive="mobile-1000">
                                            <text data-ellipsis="" data-text-size="medium">{t("LENGTH")}<i data-space-horizontal="5" data-color="red">*</i></text>
                                        </group>
                                        {index < 4 ?
                                            <group data-length="autofit">
                                                <Input
                                                    type={InputType.Number}
                                                    //{...(index === 0 ? { label: t("LENGTH") } : {})}
                                                    //label={<><text data-adaptive="mobile-1000">{t("LENGTH")}</text></>}
                                                    label={<></>}
                                                    requiredLabel={true}
                                                    control={control}
                                                    name={`coolMode.config[${index}].length`}
                                                    translateValue={true}
                                                    disabled={!data.inertia}
                                                    size="large"
                                                    //placeholder={`${t("LENGTH")}`}
                                                    placeholder={`0.00`}
                                                    dataLength="autofit"
                                                    minValue={0}
                                                    step={0.1}
                                                //loading={isFetchingDepartments}
                                                />
                                            </group>
                                            : <group></group>}
                                    </group>
                                    {index < 4 ?
                                        <group
                                            data-length="autofit"
                                            data-direction="column"
                                            data-max-length="50%" data-space-left="10"
                                        >
                                            <group data-length="autofit" data-adaptive="mobile-1000">
                                                <text data-ellipsis="" data-text-size="medium">{t("MITOYEN")}<i data-space-horizontal="5" data-color="red">*</i></text>
                                            </group>
                                            <group data-length="autofit">
                                                <Input
                                                    type={InputType.Select}
                                                    //{...(index === 0 ? { label: t("MITOYEN") } : {})}
                                                    //label={<><text data-adaptive="mobile-1000">{t("MITOYEN")}</text></>}
                                                    label={<></>}
                                                    requiredLabel={true}
                                                    control={control}
                                                    name={`coolMode.config[${index}].mitoyen`}
                                                    options={convertToSelect(data.mitoyen)}
                                                    translateValue={true}
                                                    disabled={!data.inertia}
                                                    allowSearch={true}
                                                    size="large"
                                                    //placeholder={`${t("MITOYEN")}`}
                                                    dataLength="autofit"
                                                    onChange={(e) => onmitoyenChange(e, index)}
                                                //loading={isFetchingDepartments}
                                                />
                                            </group>

                                        </group>
                                        :
                                        <group data-length="180"></group>
                                    }
                                </group>

                                <group
                                    data-width="auto"
                                    data-wrap="no"
                                    //data-background="green"
                                    data-length="forcefit-1000"
                                    data-fit="1"
                                >

                                    <group data-length="autofit" data-direction="column" data-max-length="50%" data-space-right="10">
                                        <group data-length="autofit" data-adaptive="mobile-1000">
                                            <text data-ellipsis="" data-text-size="medium">{t("SURFACE")}<i data-space-horizontal="5" data-color="red">*</i></text>
                                        </group>
                                        <group data-length="autofit">
                                            <Input
                                                type={InputType.Number}
                                                //{...(index === 0 ? { label: t("SURFACE") } : {})}
                                                //label={<><text data-adaptive="mobile-1000">{t("SURFACE")}</text></>}
                                                label={<></>}
                                                requiredLabel={true}
                                                control={control}
                                                name={`coolMode.config[${index}].surface`}
                                                translateValue={true}
                                                disabled={!data.inertia}
                                                size="large"
                                                //placeholder={`${t("SURFACE")}`}
                                                placeholder={`0.00`}
                                                dataLength="autofit"
                                                onChange={(e) => onsurfaceChange(e, index)}
                                                minValue={0}
                                                step={0.1}
                                            //loading={isFetchingDepartments}
                                            />
                                        </group>
                                    </group>

                                    <group data-length="autofit" data-direction="column" data-max-length="50%" data-space-left="10">
                                        <group data-length="autofit" data-adaptive="mobile-1000">
                                            <text data-ellipsis="" data-text-size="medium">{t("OCCULTANT")}<i data-space-horizontal="5" data-color="red">*</i></text>
                                        </group>
                                        <group data-length="autofit">
                                            <Input
                                                type={InputType.Select}
                                                //{...(index === 0 ? { label: t("OCCULTANT") } : {})}
                                                //label={<><text data-adaptive="mobile-1000">{t("OCCULTANT")}</text></>}
                                                label={<></>}
                                                requiredLabel={true}
                                                control={control}
                                                name={`coolMode.config[${index}].occultant`}
                                                options={convertToSelect(data.occultant)}
                                                translateValue={true}
                                                allowSearch={true}
                                                size="large"
                                                //placeholder={`${t("OCCULTANT")}`}
                                                dataLength="autofit"
                                                disabled={!data.inertia || formStore.config?.[index]?.surface == '0'}
                                            //loading={isFetchingDepartments}
                                            />
                                        </group>
                                    </group>
                                </group>
                            </group>
                        )
                    })}
                </group>
            </group>
        </>
    )
});

CoolMode.displayName = 'CoolMode';

export default CoolMode;